import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Copy,
  Check,
  Link2,
  Calendar,
  Lock,
  Download,
  FileDown,
  Shield,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  MessageSquare,
  Film,
  Plus,
  Trash2,
  Pencil,
} from 'lucide-react';
import {
  listRecordingShares,
  createRecordingShare,
  updateRecordingShare,
  deleteRecordingShare,
  deleteLegacyRecordingShare,
  updateShareSettings,
} from '../../../../services/recordingService';
import { toast } from 'react-toastify';
import { TIMEZONES } from '../../../../utils/timezones';

const DEFAULT_SETTINGS = {
  isPublic: true,
  requiresAuth: false,
  allowDownload: true,
  allowExport: true,
  showAINotes: false,
  showChat: false,
  includedParts: [],
  expiresAt: null,
};

const ToggleSwitch = ({ checked, onChange, color = '#00c853' }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="sr-only peer"
    />
    <div
      className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
      style={{ backgroundColor: checked ? color : undefined }}
    />
  </label>
);

/**
 * One row in the shares list. Collapsed by default; expands to show all settings
 * for that specific share. Each share has its own URL, label, and settings.
 */
function ShareRow({
  share,
  recordingName,
  allParts,
  onPatch,
  onDelete,
  isLegacy,
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(share.label || '');
  const [savingExpiration, setSavingExpiration] = useState(false);

  // Local form state mirrored from share.settings, written through onPatch.
  const settings = share.settings || {};
  const shareUrl = `${window.location.origin}/recording-preview/${share.shareToken}`;

  const [expirationDate, setExpirationDate] = useState('');
  const [expirationTime, setExpirationTime] = useState('23:59');
  const [expirationTimezone, setExpirationTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [tzDropdownOpen, setTzDropdownOpen] = useState(false);
  const [tzSearch, setTzSearch] = useState('');
  const expirationEnabled = !!settings.expiresAt;

  useEffect(() => {
    if (settings.expiresAt) {
      const d = new Date(settings.expiresAt);
      setExpirationDate(d.toISOString().split('T')[0]);
      setExpirationTime(d.toTimeString().slice(0, 5));
    }
  }, [settings.expiresAt]);

  const patchSettings = (partial) => {
    onPatch({ settings: { ...settings, ...partial } });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLabelSave = () => {
    setEditingLabel(false);
    if (labelDraft !== (share.label || '')) {
      onPatch({ label: labelDraft });
    }
  };

  const handlePartToggle = (idx) => {
    const current = settings.includedParts || [];
    const allIncluded = current.length === 0 || current.length === allParts.length;
    let next;
    if (allIncluded) {
      // First explicit selection: start with everything except idx
      next = allParts.map((_, i) => i).filter((i) => i !== idx);
    } else if (current.includes(idx)) {
      next = current.filter((i) => i !== idx);
    } else {
      next = [...current, idx].sort((a, b) => a - b);
    }
    if (next.length === 0) {
      toast.warning('At least one part must be included');
      return;
    }
    patchSettings({ includedParts: next.length === allParts.length ? [] : next });
  };

  const isPartIncluded = (idx) => {
    const current = settings.includedParts || [];
    if (current.length === 0) return true; // empty = all
    return current.includes(idx);
  };

  const handleExpirationToggle = (enabled) => {
    if (!enabled) {
      patchSettings({ expiresAt: null });
      setExpirationDate('');
      setExpirationTime('23:59');
      toast.success('Expiration removed');
    } else {
      // Just open the date picker — actual save happens on Save Expiration click
      setExpirationDate(new Date().toISOString().split('T')[0]);
    }
  };

  const handleExpirationSave = async () => {
    if (!expirationDate) {
      toast.error('Please select an expiration date');
      return;
    }
    setSavingExpiration(true);
    try {
      const dateTimeStr = `${expirationDate}T${expirationTime || '23:59'}:00`;
      const utcRef = new Date(dateTimeStr + 'Z');
      const tzStr = utcRef.toLocaleString('en-US', { timeZone: expirationTimezone });
      const inTz = new Date(tzStr);
      const offsetMs = utcRef.getTime() - inTz.getTime();
      const expiresAt = new Date(new Date(dateTimeStr).getTime() + offsetMs).toISOString();
      patchSettings({ expiresAt });
      toast.success('Expiration date saved');
    } catch (err) {
      console.error('Failed to save expiration:', err);
      toast.error('Failed to save expiration');
    } finally {
      setSavingExpiration(false);
    }
  };

  const displayLabel =
    share.label ||
    (isLegacy ? 'Default share' : `Untitled link · ${new Date(share.createdAt || Date.now()).toLocaleDateString()}`);

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.03]">
      {/* Header row */}
      <div className="flex items-center gap-2 p-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-white/60" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/60" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          {editingLabel ? (
            <div className="flex items-center gap-1">
              <input
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLabelSave();
                  if (e.key === 'Escape') {
                    setLabelDraft(share.label || '');
                    setEditingLabel(false);
                  }
                }}
                placeholder="Recipient name (optional)"
                autoFocus
                disabled={isLegacy}
                className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-[#00c853]/50"
              />
              <button
                onClick={handleLabelSave}
                className="p-1 hover:bg-white/10 rounded text-[#00c853]"
                title="Save"
              >
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-white truncate">{displayLabel}</span>
              {!isLegacy && (
                <button
                  onClick={() => {
                    setLabelDraft(share.label || '');
                    setEditingLabel(true);
                  }}
                  className="p-0.5 hover:bg-white/10 rounded text-white/40 hover:text-white/70 flex-shrink-0"
                  title="Edit label"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          <div className="text-xs text-white/40 truncate font-mono mt-0.5">{shareUrl}</div>
        </div>
        <button
          onClick={handleCopy}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0"
          title="Copy share link"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span className="text-xs text-white">{copied ? 'Copied' : 'Copy'}</span>
        </button>
        <button
          onClick={() => onDelete(share, isLegacy)}
          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
          title={isLegacy ? 'Revoke legacy share link' : 'Revoke this share link'}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded settings */}
      {expanded && (
        <div className="border-t border-white/10 p-3 space-y-2">
          {/* Public toggle */}
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <div>
              <div className="text-sm font-medium text-white mb-0.5">Public Link</div>
              <div className="text-xs text-white/50">Anyone with the link can view</div>
            </div>
            <ToggleSwitch
              checked={!!settings.isPublic}
              onChange={(e) => patchSettings({ isPublic: e.target.checked })}
            />
          </div>

          {settings.isPublic && (
            <>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-white/50" />
                  <div>
                    <div className="text-sm font-medium text-white mb-0.5">Require Login</div>
                    <div className="text-xs text-white/50">Viewers must be logged in</div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={!!settings.requiresAuth}
                  onChange={(e) => patchSettings({ requiresAuth: e.target.checked })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-white/50" />
                  <div>
                    <div className="text-sm font-medium text-white mb-0.5">Allow Download</div>
                    <div className="text-xs text-white/50">Viewers can download the video</div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={settings.allowDownload !== false}
                  onChange={(e) => patchSettings({ allowDownload: e.target.checked })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileDown className="w-4 h-4 text-white/50" />
                  <div>
                    <div className="text-sm font-medium text-white mb-0.5">Allow Export</div>
                    <div className="text-xs text-white/50">Viewers can export notes & transcript</div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={settings.allowExport !== false}
                  onChange={(e) => patchSettings({ allowExport: e.target.checked })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-white/50" />
                  <div>
                    <div className="text-sm font-medium text-white mb-0.5">Show AI Notes</div>
                    <div className="text-xs text-white/50">Summary, action items & key questions</div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={!!settings.showAINotes}
                  onChange={(e) => patchSettings({ showAINotes: e.target.checked })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-white/50" />
                  <div>
                    <div className="text-sm font-medium text-white mb-0.5">Show Chat</div>
                    <div className="text-xs text-white/50">Meeting chat messages</div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={!!settings.showChat}
                  onChange={(e) => patchSettings({ showChat: e.target.checked })}
                />
              </div>

              {/* Video parts selection (only for multi-part recordings) */}
              {allParts.length > 1 && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Film className="w-4 h-4 text-white/50" />
                    <div>
                      <div className="text-sm font-medium text-white mb-0.5">Include Video Parts</div>
                      <div className="text-xs text-white/50">Select which parts are visible in this share</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {allParts.map((part, idx) => (
                      <label
                        key={part.id || part._id || idx}
                        className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white/5 rounded-lg transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isPartIncluded(idx)}
                          onChange={() => handlePartToggle(idx)}
                          className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#00c853] focus:ring-[#00c853]/50"
                        />
                        <span className="text-sm text-white">
                          Part {idx + 1}
                          {part.duration > 0 && (
                            <span className="text-white/40 ml-2">
                              ({Math.floor(part.duration / 60)}:{String(part.duration % 60).padStart(2, '0')})
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Expiration */}
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-white/50" />
                    <div>
                      <div className="text-sm font-medium text-white mb-0.5">Set Expiration Date</div>
                      <div className="text-xs text-white/50">Recording won't be accessible after this date</div>
                    </div>
                  </div>
                  <ToggleSwitch
                    checked={expirationEnabled}
                    onChange={(e) => handleExpirationToggle(e.target.checked)}
                    color="#6a1fbf"
                  />
                </div>

                {expirationEnabled && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-white/50 mb-1">Date</label>
                        <input
                          type="date"
                          value={expirationDate}
                          onChange={(e) => setExpirationDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#6a1fbf]/50 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/50 mb-1">Time</label>
                        <input
                          type="time"
                          value={expirationTime}
                          onChange={(e) => setExpirationTime(e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#6a1fbf]/50 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label className="block text-xs text-white/50 mb-1">Timezone</label>
                      <button
                        type="button"
                        onClick={() => setTzDropdownOpen(!tzDropdownOpen)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-left flex items-center justify-between focus:border-[#6a1fbf]/50 focus:outline-none"
                      >
                        <span className="truncate">
                          {TIMEZONES.find((t) => t.value === expirationTimezone)?.label ||
                            expirationTimezone.replace(/_/g, ' ')}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-white/50 flex-shrink-0 transition-transform ${
                            tzDropdownOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {tzDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => {
                              setTzDropdownOpen(false);
                              setTzSearch('');
                            }}
                          />
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#0a1929] border border-white/20 rounded-lg shadow-xl z-20 overflow-hidden">
                            <div className="p-2 border-b border-white/10">
                              <input
                                type="text"
                                value={tzSearch}
                                onChange={(e) => setTzSearch(e.target.value)}
                                placeholder="Search timezone..."
                                autoFocus
                                className="w-full px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#6a1fbf]/50"
                              />
                            </div>
                            <div className="max-h-40 overflow-y-auto">
                              {TIMEZONES.filter((tz) => {
                                const q = tzSearch.toLowerCase();
                                return (
                                  tz.label.toLowerCase().includes(q) ||
                                  tz.value.toLowerCase().includes(q) ||
                                  tz.offset.toLowerCase().includes(q)
                                );
                              }).map((tz) => (
                                <button
                                  key={tz.value}
                                  type="button"
                                  onClick={() => {
                                    setExpirationTimezone(tz.value);
                                    setTzDropdownOpen(false);
                                    setTzSearch('');
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                                    tz.value === expirationTimezone
                                      ? 'bg-[#6a1fbf]/30 text-white'
                                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                                  }`}
                                >
                                  {tz.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={handleExpirationSave}
                      disabled={savingExpiration || !expirationDate}
                      className="w-full px-4 py-2 bg-[#6a1fbf] hover:bg-[#6a1fbf]/80 disabled:opacity-50 rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {savingExpiration ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4" /> Save Expiration
                        </>
                      )}
                    </button>
                    {settings.expiresAt && (
                      <p className="text-xs text-white/40 text-center">
                        Currently expires:{' '}
                        {new Date(settings.expiresAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                          timeZoneName: 'short',
                        })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const RecordingShareModal = ({ recording, allParts = [], onClose, onUpdate }) => {
  const recId = recording?.id || recording?._id;

  const [shares, setShares] = useState([]);
  const [legacy, setLegacy] = useState(null); // { shareToken, settings }
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingShareIds, setSavingShareIds] = useState(new Set());
  const saveTimersRef = useRef(new Map());
  // Tracks the latest local share state per shareId so the debounced saver
  // always reads the most recent value rather than relying on closures.
  const sharesRef = useRef([]);
  // Same idea for the legacy single-share so its toggles persist correctly.
  const legacyRef = useRef(null);

  // Keep the refs in sync with state
  useEffect(() => {
    sharesRef.current = shares;
  }, [shares]);

  useEffect(() => {
    legacyRef.current = legacy;
  }, [legacy]);

  const fetchShares = useCallback(async () => {
    if (!recId) return;
    try {
      setLoading(true);
      const res = await listRecordingShares(recId);
      if (res?.success) {
        const next = res.shares || [];
        setShares(next);
        sharesRef.current = next;
        setLegacy(res.legacy || null);
      }
    } catch (err) {
      console.error('Failed to load shares:', err);
      toast.error('Failed to load share links');
    } finally {
      setLoading(false);
    }
  }, [recId]);

  useEffect(() => {
    fetchShares();
    // Cleanup any pending debounced saves on unmount
    return () => {
      saveTimersRef.current.forEach((t) => clearTimeout(t));
      saveTimersRef.current.clear();
    };
  }, [fetchShares]);

  // Patch the LEGACY single-share (Recording.shareSettings) — same instant
  // feedback + debounced save pattern as patchShare. Calls the legacy
  // PUT /:recordingId/share endpoint which exists for backward compat.
  const patchLegacyShare = (partial) => {
    const current = legacyRef.current || { shareToken: null, settings: {} };
    const nextSettings = { ...(current.settings || {}), ...(partial.settings || {}) };
    const next = { ...current, settings: nextSettings };
    legacyRef.current = next;
    setLegacy(next);

    // Debounced save (reuse the timers map with a fixed key)
    const key = '__legacy__';
    const existing = saveTimersRef.current.get(key);
    if (existing) clearTimeout(existing);
    const t = setTimeout(async () => {
      saveTimersRef.current.delete(key);
      const latest = legacyRef.current?.settings || {};
      try {
        setSavingShareIds((prev) => {
          const n = new Set(prev);
          n.add(key);
          return n;
        });
        await updateShareSettings(recId, latest);
      } catch (err) {
        console.error('Failed to save legacy share:', err);
        toast.error('Failed to save share settings');
      } finally {
        setSavingShareIds((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    }, 400);
    saveTimersRef.current.set(key, t);
  };

  // Patch a share locally for instant feedback, then debounce a server save.
  const patchShare = (shareId, partial) => {
    // 1. Compute the next state and update both React state AND the ref synchronously
    //    so the debounced saver below can read the latest values without races.
    const nextShares = sharesRef.current.map((s) => {
      if ((s.id || s._id) !== shareId) return s;
      const merged = { ...s };
      if (partial.label !== undefined) merged.label = partial.label;
      if (partial.settings) {
        merged.settings = { ...(s.settings || {}), ...partial.settings };
      }
      return merged;
    });
    sharesRef.current = nextShares;
    setShares(nextShares);

    // 2. Debounced save (cancel any pending save for this share)
    const existing = saveTimersRef.current.get(shareId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(async () => {
      saveTimersRef.current.delete(shareId);
      const latest = sharesRef.current.find((s) => (s.id || s._id) === shareId);
      if (!latest) return;
      try {
        setSavingShareIds((prev) => {
          const next = new Set(prev);
          next.add(shareId);
          return next;
        });
        await updateRecordingShare(shareId, { label: latest.label, settings: latest.settings });
      } catch (err) {
        console.error('Failed to save share:', err);
        toast.error('Failed to save share settings');
      } finally {
        setSavingShareIds((prev) => {
          const next = new Set(prev);
          next.delete(shareId);
          return next;
        });
      }
    }, 400);
    saveTimersRef.current.set(shareId, t);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      // Sensible defaults: public, full access enabled. Owner can lock down per-share after.
      const res = await createRecordingShare(recId, {
        label: '',
        settings: { ...DEFAULT_SETTINGS },
      });
      if (res?.success && res.share) {
        const next = [...sharesRef.current, res.share];
        sharesRef.current = next;
        setShares(next);
        toast.success('New share link created');
      }
    } catch (err) {
      console.error('Failed to create share:', err);
      toast.error('Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (share, isLegacy) => {
    const confirmMsg = isLegacy
      ? 'Revoke the legacy share link? Anyone holding this URL will lose access immediately.'
      : 'Revoke this share link? Anyone holding this URL will lose access immediately.';
    if (!window.confirm(confirmMsg)) return;
    try {
      if (isLegacy) {
        await deleteLegacyRecordingShare(recId);
        setLegacy(null);
        toast.success('Legacy share link revoked');
      } else {
        const sid = share.id || share._id;
        await deleteRecordingShare(sid);
        const next = sharesRef.current.filter((s) => (s.id || s._id) !== sid);
        sharesRef.current = next;
        setShares(next);
        toast.success('Share link revoked');
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to revoke share:', err);
      toast.error('Failed to revoke share link');
    }
  };

  // Build a synthetic "share" entry for the legacy single-share so it can use the
  // same row UI. The row is read-only-ish for label and settings — only Copy + Delete work.
  const legacyRow = legacy
    ? {
        id: '__legacy__',
        shareToken: legacy.shareToken,
        label: '',
        settings: legacy.settings || {},
        createdAt: recording?.createdAt,
      }
    : null;

  const totalSaving = savingShareIds.size > 0;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#001233] border border-white/10 rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#00c853]" />
            <h2 className="text-xl font-semibold text-white">Share Recording</h2>
          </div>
          <div className="flex items-center gap-2">
            {totalSaving && <span className="text-xs text-white/40">Saving...</span>}
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>
        </div>

        <p className="text-sm text-white/60 mb-4">
          Create multiple share links with different settings — give each recipient exactly the access you want.
          Revoke any link at any time.
        </p>

        {/* Shares list */}
        <div className="space-y-3 mb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : (
            <>
              {legacyRow && (
                <ShareRow
                  share={legacyRow}
                  recordingName={recording?.name}
                  allParts={allParts}
                  onPatch={patchLegacyShare}
                  onDelete={handleDelete}
                  isLegacy
                />
              )}
              {shares.map((share) => (
                <ShareRow
                  key={share.id || share._id}
                  share={share}
                  recordingName={recording?.name}
                  allParts={allParts}
                  onPatch={(partial) => patchShare(share.id || share._id, partial)}
                  onDelete={handleDelete}
                />
              ))}
              {!legacyRow && shares.length === 0 && (
                <div className="text-center py-8 text-white/50 text-sm border border-dashed border-white/10 rounded-lg">
                  No share links yet. Create one to start sharing this recording.
                </div>
              )}
            </>
          )}
        </div>

        {/* Create new share button */}
        <button
          onClick={handleCreate}
          disabled={creating || loading}
          className="w-full px-4 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 rounded-lg text-white text-sm font-medium transition-opacity flex items-center justify-center gap-2 mb-3"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New Share Link
        </button>

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default RecordingShareModal;
